import { Button } from 'UI';
import { PaymentDtoLevelEnum, PaymentDtoTypeEnum } from 'api/generated';
import httpClient from 'api/https-client';
import { FC, useState } from 'react';
import { selectors, useAppSelector } from 'store';
import styled from 'styled-components';
import { respondToWidth } from 'styles/general/respondTo';
import { trackAmplitudeEvent } from 'utils/trackAmplitudeEvent';

// MVP === gold tier, held via a lifetime purchase, an installment plan, or a recurring subscription.
const MVP_PAID_TYPES: PaymentDtoTypeEnum[] = [
  PaymentDtoTypeEnum.Purchase, // lifetime MVP / gold
  PaymentDtoTypeEnum.Subscription, // monthly / annual MVP subscription
  PaymentDtoTypeEnum.Installment, // MVP installment plan
];

const CoachesOnlineBonus: FC = () => {
  const level = useAppSelector(selectors.courses.selectCurrentPaymentLevel);
  const type = useAppSelector(selectors.courses.selectCurrentPaymentType);
  const [isLoading, setIsLoading] = useState(false);

  const isMvpGold = level === PaymentDtoLevelEnum.Gold && !!type && MVP_PAID_TYPES.includes(type);

  // Only MVP / gold members ever see this section (installments, subscriptions, or lifetime purchase).
  if (!isMvpGold) {
    return null;
  }

  const handleClaim = async () => {
    if (isLoading) {
      return;
    }

    setIsLoading(true);
    trackAmplitudeEvent('CoachesOnline Claim Pressed');

    try {
      // Mint a FRESH, short-lived (10 min) handoff URL on every click — never cache it.
      const { status, data } = await httpClient.get<{ url: string }>('/coachesonline/handoff-url');

      if (status >= 200 && status < 300 && data?.url) {
        window.open(data.url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      // Graceful degrade — surface nothing scary to the member; logged for debugging.
      console.error('Failed to build CoachesOnline handoff URL', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Section>
      <Badge>★ MVP bonus</Badge>
      <TextBlock>
        <Title>Your free CoachesOnline website</Title>
        <Description>
          As an MVP member, you get a free professional coaching website on CoachesOnline — claim it to start setting it
          up.
        </Description>
      </TextBlock>
      <StyledButton variant="primary" size="small" isLoading={isLoading} onClick={handleClaim}>
        Claim your CoachesOnline site →
      </StyledButton>
    </Section>
  );
};

export default CoachesOnlineBonus;

// Section is gated to MVP / gold members only.
// 🚧 Pre-launch toggle: uncomment the `display: none;` line below to hide the entire section.
const Section = styled.div`
  /*display: flex;*/
  display: none;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;

  width: 100%;
  margin-top: 16px;
  padding: 20px 24px;
  border-radius: 16px;
  background-image: linear-gradient(135deg, #d8ccff 0%, #e8cdff 55%, #f6d2f6 100%);
  box-shadow: 0 12px 28px -14px rgba(120, 80, 220, 0.55);

  ${respondToWidth.s`
    padding: 16px;
  `}
`;

// The card keeps a fixed light gradient in both themes, so text uses fixed dark colors
// (theme neutrals would turn white in dark mode and become unreadable on this card).
const Badge = styled.span`
  ${({ theme: { typography } }) => typography.footnote_semibold_12};
  color: ${({ theme: { colors } }) => colors.primary[1]};
  background: rgba(255, 255, 255, 0.7);
  padding: 4px 10px;
  border-radius: 999px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Title = styled.div`
  ${({ theme: { typography } }) => typography.headline_semibold_18};
  color: #2b2b3a;
`;

const Description = styled.div`
  ${({ theme: { typography } }) => typography.body_basic_medium_14};
  color: #50506a;
  line-height: 1.45;
  max-width: 460px;
`;

const StyledButton = styled(Button)`
  width: auto;
  margin-top: 4px;
  padding: 10px 24px;
  white-space: nowrap;

  ${respondToWidth.s`
    width: 100%;
  `}
`;
